import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Alert, Select, Steps } from 'antd';
import {
  faArrowLeft,
  faArrowRight,
  faBolt,
  faBuilding,
  faCalendar,
  faCheck,
  faCircleCheck,
  faClock,
  faComment,
  faFire,
  faKey,
  faPause,
  faPhone,
  faPlay,
  faThumbsDown,
  faThumbsUp,
  faUser,
  faWrench,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { useAction } from '../../queries/actions';
import { fixDuration } from '../../components/CallRecording';
import { useCallRecordingQuery } from '../../queries/calls';
import { useVendorOptions } from '../../queries/vendors';
import { ATTEMPTS, OPEN_LANES, PRIORITIES, clock, laneOf, outcomeLabel, prioOf, roleLabel, stampOf, typeLabel } from '../../utils/actions';
import { callerLabel, formatPhone } from '../../utils/callLog';
import { assigneeName } from '../../queries/scheduling';
import { PrioChip, ageOf } from './Card';
import { Av, Btn, Chip, I, LockBtn, Seg, cx } from './ui';

const CALLER = {
  prospect: 'Prospect',
  tenant: 'Tenant',
  owner: 'Owner',
  owner_lead: 'Owner lead',
  vendor: 'Vendor',
  other: 'Other'
};
const dur = (secs = 0) => `${Math.floor(secs / 60)}:${String(Math.round(secs % 60)).padStart(2, '0')}`;
const attemptLabel = (key) => ATTEMPTS.find((a) => a[0] === key)?.[1] || key;

function Sec({ title, right, children }) {
  return (
    <div className="ac-sec">
      <div className="ac-st">
        <span>{title}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

// The timestamp is already its own gray description, so drop a trailing time the
// activity text may repeat (", 4:21 PM CT" or " · 4:39 PM").
const stripTime = (s) => String(s).replace(/\s*[,·]\s*\d{1,2}:\d{2}\s*(?:AM|PM)(?:\s*CT)?\s*$/i, '');

const Log = ({ rows }) => (
  <div className="pl-timeline">
    <Steps
      direction="vertical"
      size="small"
      progressDot
      current={rows.length}
      items={rows.map((r) => ({ title: stripTime(r[0]), description: r[1] }))}
    />
  </div>
);

function Player({ call }) {
  const { data } = useCallRecordingQuery(call._id);
  const audio = useRef(null);
  const [on, setOn] = useState(false);
  const [cur, setCur] = useState(0);
  const [len, setLen] = useState(0);
  const total = len || call.durationSec || 0;
  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };
  const seek = (frac) => {
    const el = audio.current;
    if (el && total) el.currentTime = Math.max(0, Math.min(1, frac)) * total;
  };
  return (
    <div className="ac-player">
      <button type="button" className="ac-play" aria-label={on ? 'Pause recording' : 'Play recording'} disabled={!data?.url} onClick={toggle}>
        <I icon={on ? faPause : faPlay} />
      </button>
      <div
        className="ac-bar"
        role="slider"
        tabIndex={0}
        aria-label="Seek recording"
        aria-valuemin={0}
        aria-valuemax={Math.round(total)}
        aria-valuenow={Math.round(Math.min(cur, total))}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          seek((e.clientX - r.left) / r.width);
        }}
        onKeyDown={(e) => {
          if (!total) return;
          if (e.key === 'ArrowRight') seek((cur + 5) / total);
          else if (e.key === 'ArrowLeft') seek((cur - 5) / total);
        }}
      >
        <span style={{ width: `${total ? Math.min(100, (cur / total) * 100) : 0}%` }} />
      </div>
      <span className="m-sub num nowrap">{data?.url ? `${dur(Math.min(cur, total))} / ${dur(total)}` : 'Recording unavailable'}</span>
      {data?.url && (
        <audio
          ref={audio}
          src={data.url}
          preload="metadata"
          onLoadedMetadata={(e) => {
            fixDuration(e);
            if (Number.isFinite(e.target.duration)) setLen(e.target.duration);
          }}
          onDurationChange={(e) => {
            if (Number.isFinite(e.target.duration)) setLen(e.target.duration);
          }}
          onPlay={() => setOn(true)}
          onPause={() => setOn(false)}
          onTimeUpdate={(e) => setCur(e.target.currentTime)}
          onEnded={() => setOn(false)}
        />
      )}
    </div>
  );
}

function NoteBox({ onAdd }) {
  const [t, setT] = useState('');
  return (
    <div className="ac-nb">
      <textarea
        className="m-inp"
        rows={2}
        placeholder="Add a note for the team"
        value={t}
        maxLength={400}
        onChange={(e) => setT(e.target.value)}
        aria-label="Add a note"
      />
      <div className="m-row">
        <div className="grow" />
        <Btn
          sm
          disabled={!t.trim()}
          onClick={() => {
            onAdd(t.trim());
            setT('');
          }}
        >
          Add note
        </Btn>
      </div>
    </div>
  );
}

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

function EditFields({ fields, onApprove, onCancel }) {
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.key, f.value])));
  const { data: vendors = [] } = useVendorOptions({
    enabled: fields.some((f) => f.input === 'vendor')
  });
  const set = (key) => (e) => setValues({ ...values, [key]: e.target.value });
  return (
    <div className="m-col gap8">
      {fields.map((f) => (
        <div key={f.key} className="m-field">
          <label htmlFor={`edit-${f.key}`}>{f.label}</label>
          {f.input === 'textarea' || f.value.length > 40 ? (
            <textarea id={`edit-${f.key}`} className="m-inp" rows={3} value={values[f.key]} onChange={set(f.key)} />
          ) : f.input === 'datetime' ? (
            <input
              id={`edit-${f.key}`}
              className="m-inp"
              type="datetime-local"
              value={toLocalInput(values[f.key])}
              onChange={(e) =>
                setValues({
                  ...values,
                  [f.key]: new Date(e.target.value).toISOString()
                })
              }
            />
          ) : f.input === 'vendor' ? (
            <select id={`edit-${f.key}`} className="m-inp" value={values[f.key]} onChange={set(f.key)}>
              {vendors.map((v) => (
                <option key={v._id} value={v._id}>
                  {v.company}
                </option>
              ))}
            </select>
          ) : (
            <input id={`edit-${f.key}`} className="m-inp" value={values[f.key]} onChange={set(f.key)} />
          )}
        </div>
      ))}
      <div className="m-row">
        <Btn kind="primary" sm onClick={() => onApprove(values)}>
          Approve edited
        </Btn>
        <Btn sm onClick={onCancel}>
          Cancel
        </Btn>
      </div>
    </div>
  );
}

function Batch({ x, tickets, locked, run }) {
  const entered = tickets.filter((t) => t.pwEnteredAt || t.externalId);
  const left = tickets.filter((t) => !t.pwEnteredAt && !t.externalId).map((t) => t._id);
  return (
    <div className="m-batch">
      <div className="m-row between wrap">
        <div className="b sm">
          {entered.length} of {tickets.length} entered in {x.pmSystem || 'your PM system'}
        </div>
        {locked ? (
          <LockBtn label="Mark all entered" why={locked} sm />
        ) : (
          <Btn sm kind="primary" disabled={!left.length} onClick={() => run('batch', { ticketIds: left, entered: true }, true)}>
            Mark all entered
          </Btn>
        )}
      </div>
      {tickets.map((t) => {
        const on = Boolean(t.pwEnteredAt || t.externalId);
        return (
          <div key={t._id} className="m-brow">
            <label className="m-check">
              <input
                type="checkbox"
                checked={on}
                disabled={Boolean(locked) || Boolean(t.externalId)}
                onChange={() => run('batch', { ticketIds: [t._id], entered: !on }, !on && left.length === 1)}
              />
              <span className="b">{t.ticketNumber}</span>
            </label>
            <span className="grow sm">
              {t.propertyUnit} · {t.issue}
            </span>
            {on && <Chip fam="ok">Entered</Chip>}
          </div>
        );
      })}
      {x.lane === 'done' && <span className="m-sub">Completed.</span>}
    </div>
  );
}

export default function ActionCenter({
  x,
  tz,
  now,
  agent,
  phone,
  owners,
  team = [],
  why,
  canManage,
  run,
  onPrimary,
  onApproveEdited,
  onFinish,
  onFlag,
  onCall,
  onSchedule,
  onClose
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const { data } = useAction(x._id);
  const calls = data?.calls || [];
  const ticket = data?.ticket;
  const booking = data?.booking;
  const tickets = data?.tickets || [];
  const call = calls[0];
  const dn = x.lane === 'done';
  const name = callerLabel(x);
  const property = [x.property, x.unit && !String(x.property).includes(x.unit) ? x.unit : ''].filter(Boolean).join(', ') || ticket?.propertyUnit;
  const p = prioOf(x);
  const age = ageOf(x, now);
  const lane = laneOf(x.lane);
  const created = `${x.by === 'Assistant' ? agent : x.by} created this card`;
  const ownerName = (key) => (key === '' ? 'Unassigned' : key === 'agent_zero' ? 'Agent Zero team' : owners[key] || roleLabel(key, agent));
  const go = (path, state) => {
    onClose();
    navigate(path, state ? { state } : undefined);
  };

  const subline = [name || `${agent} and the system`, CALLER[x.callerRole], typeLabel(x.type)].filter(Boolean).join(' · ');
  const head = (
    <div className="ac-head">
      {phone && (
        <button type="button" className="ac-back" onClick={onClose} aria-label="Back">
          <I icon={faArrowLeft} />
        </button>
      )}
      {name ? (
        <Av name={name} />
      ) : (
        <span className="m-av">
          <I icon={faBolt} />
        </span>
      )}
      <div className="ac-hm">
        <h2 id="ac-title">{x.title}</h2>
        <div className="ac-hs">{subline}</div>
      </div>
      {!phone && (
        <button type="button" className="ac-x" onClick={onClose} aria-label="Close">
          <I icon={faXmark} />
        </button>
      )}
    </div>
  );

  const chips = (
    <div className="ac-chips">
      <PrioChip p={p} ac />
      {age ? (
        <span className={cx('ac-age', age[0])}>
          <I icon={age[0] === 'red' ? faFire : faClock} />
          {age[1]}
        </span>
      ) : x.dueAt && !dn ? (
        <span className="ac-age">
          <I icon={faClock} />
          Due {stampOf(x.dueAt, tz, now)}
        </span>
      ) : null}
      <span className={`m-chip c-${lane.chip} tagx`}>{lane.label}</span>
      {dn && x.outcome && <span className="m-chip c-ok tagx">{outcomeLabel(x.outcome)}</span>}
      <span className="m-sub">Created {stampOf(x.arrivedAt, tz, now)}</span>
    </div>
  );

  const linked = ticket
    ? {
        icon: faWrench,
        t: `${ticket.ticketNumber} · ${ticket.issue}`,
        sub: [ticket.propertyUnit, ticket.assignedTo].filter(Boolean).join(' · '),
        chip: [ticket.status.replace('_', ' '), 'info'],
        lk: ['Open work order', () => go('/maintenance')],
        label: 'work order'
      }
    : booking
      ? {
          icon: faCalendar,
          t: 'Showing',
          sub: `${stampOf(booking.start, tz, now)}${property ? ` · ${property}` : ''}`,
          chip: [booking.status === 'cancelled' ? 'Cancelled' : 'Scheduled', booking.status === 'cancelled' ? 'neutral' : 'ok'],
          lk: ['Open in Calendar', () => go('/scheduling')],
          label: 'showing'
        }
      : x.kind === 'batch'
        ? {
            icon: faBuilding,
            t: `${x.pmSystem || 'PM system'} entry · ${tickets.length} work orders`,
            sub: `${tickets.filter((t) => t.pwEnteredAt || t.externalId).length} of ${tickets.length} entered`,
            chip: [`${tickets.filter((t) => t.pwEnteredAt || t.externalId).length} of ${tickets.length}`, 'warn'],
            label: 'item'
          }
        : null;

  const ctx = [
    name && [
      'Contact',
      x.contact?.phone ? (
        <button type="button" className="aa-link" onClick={() => go('/inbox', { contactNumber: x.contact.phone, openProfile: true })}>
          {name}
        </button>
      ) : (
        name
      )
    ],
    x.contact?.phone && ['Phone', formatPhone(x.contact.phone)],
    x.contact?.email && ['Email', x.contact.email],
    property && ['Property or unit', property],
    ticket && ['Work order', `${ticket.ticketNumber} · ${ticket.tier}`],
    [
      'Source',
      call
        ? `Call · ${stampOf(call.startedAt, tz, now)} · ${dur(call.durationSec)}`
        : x.kind === 'batch'
          ? `${x.pmSystem || 'PM system'} batch`
          : x.by === 'Assistant'
            ? agent
            : x.by
    ],
    x.lane !== 'done' && x.wait !== undefined && ['Waiting on', ownerName(x.wait)]
  ].filter(Boolean);

  const history = [
    [`Created by ${x.by === 'Assistant' ? agent : x.by}${call ? ' from a call' : ''}`, stampOf(x.arrivedAt, tz, now)],
    ...(x.ladder?.steps || []).map((s) => [`${s.who} · ${s.state}${s.note ? ` · ${s.note}` : ''}`, s.when ? clock(s.when, tz) : '']),
    ...(x.attempts || []).map((a) => [`Attempt: ${attemptLabel(a.what)} · ${a.by}`, stampOf(a.at, tz, now)])
  ];

  const links = (
    <div className="ac-links">
      {x.contact?.phone && (
        <button type="button" className="aa-link sm" onClick={() => go('/inbox', { contactNumber: x.contact.phone, openProfile: true })}>
          <I icon={faUser} /> Open profile
        </button>
      )}
      {ticket && (
        <button type="button" className="aa-link sm" onClick={() => go('/maintenance')}>
          <I icon={faWrench} /> Open work order
        </button>
      )}
      {booking && (
        <button type="button" className="aa-link sm" onClick={() => go('/scheduling')}>
          <I icon={faCalendar} /> Open in Calendar
        </button>
      )}
    </div>
  );
  const left = (
    <div className="ac-left">
      {x.why && (
        <div className="ac-why">
          <div className="ac-whyh">
            <I icon={faBolt} />
            Why this is here
          </div>
          <div className="sm">{x.why}</div>
        </div>
      )}
      {linked && (
        <Sec title={`Linked ${linked.label}`}>
          <div className="ac-link">
            <span className="ac-li">
              <I icon={linked.icon} />
            </span>
            <div className="grow">
              <div className="sm b">{linked.t}</div>
              <div className="m-sub">{linked.sub}</div>
            </div>
            <Chip fam={linked.chip[1]}>{linked.chip[0]}</Chip>
          </div>
          {linked.lk && (
            <button type="button" className="aa-link sm" onClick={linked.lk[1]}>
              {linked.lk[0]} <I icon={faArrowRight} />
            </button>
          )}
          {x.kind === 'batch' && <Batch x={x} tickets={tickets} locked={why} run={run} />}
        </Sec>
      )}
      <Sec title="Summary">
        <div className="sm">
          {x.title}
          {name ? ` for ${name}` : ''}.{x.suggest ? ` ${agent} suggests: ${x.suggest}` : ''}
        </div>
        {x.approve?.send && (
          <div className="ac-draft">
            <div className="m-eb">Drafted reply</div>
            <div className="sm">{x.approve.send}</div>
          </div>
        )}
        {dn && (
          <div className="ac-done">
            <I icon={faCircleCheck} />
            <span className="sm">
              {x.resolvedBy?.role
                ? `Completed by ${x.resolvedBy.name || x.resolvedBy.role}, ${stampOf(x.resolvedAt, tz, now)}. Note: `
                : `Done by ${agent}. `}
              {x.result}
            </span>
          </div>
        )}
      </Sec>
      {editing && (
        <Sec title="Edit">
          <EditFields fields={x.approve.edit} onApprove={(values) => onApproveEdited(x, values)} onCancel={() => setEditing(false)} />
        </Sec>
      )}
      <Sec title="Context">
        <div className="ac-grid">
          {ctx.map((r) => (
            <div key={r[0]} className="ac-gc">
              <div className="ac-gl">{r[0]}</div>
              <div className="sm">{r[1]}</div>
            </div>
          ))}
        </div>
        {links}
      </Sec>
      {call && (
        <Sec title="Call recording">
          <Player call={call} />
          {call.summary && (
            <div className="ac-tr">
              <div className="ac-tl">
                <span className="ac-ts">SUMMARY</span>
                <span className="sm">{call.summary}</span>
              </div>
            </div>
          )}
          <button type="button" className="aa-link sm" onClick={() => onCall(call._id)}>
            Open in Call Logs <I icon={faArrowRight} />
          </button>
        </Sec>
      )}
      {dn && x.by === 'Assistant' && canManage && (
        <Sec title="Was this right?">
          {x.wtr?.state === 'yes' ? (
            <Chip fam="ok" icon={faCheck}>
              Marked right by {x.wtr.by}
            </Chip>
          ) : x.wtr?.state === 'flagged' ? (
            <Chip fam="warn">Flagged: not right</Chip>
          ) : (
            <div className="m-row wrap">
              <Btn sm icon={faThumbsUp} onClick={() => run('right')}>
                Yes
              </Btn>
              <Btn sm icon={faThumbsDown} onClick={() => onFlag(x)}>
                Not right
              </Btn>
            </div>
          )}
        </Sec>
      )}
      <Sec title="History">
        <Log rows={history} />
      </Sec>
    </div>
  );

  const off = dn || Boolean(why);
  const right = (
    <div className="ac-right">
      <Sec title="Owner">
        {(() => {
          const members = team.filter((p) => p.teamRole);
          const current = members.find((p) => (x.mine?.userId ? String(x.mine.userId) === String(p._id) : x.wait === p.teamRole));
          const curVal = current ? current._id : x.wait === 'agent_zero' ? 'agent_zero' : '';
          const options = [
            ...members.map((p) => ({ value: p._id, label: assigneeName(p) })),
            { value: 'agent_zero', label: 'Agent Zero team' },
            { value: '', label: 'Unassigned' },
          ];
          return (
            <Select
              aria-label="Owner"
              style={{ width: '100%' }}
              size="large"
              value={curVal}
              disabled={off}
              options={options}
              getPopupContainer={(n) => n.parentNode}
              onChange={(v) => v !== curVal && run('reassign', v === 'agent_zero' || v === '' ? { role: v } : { userId: v })}
            />
          );
        })()}
      </Sec>
      <Sec title="Priority">
        <Seg items={PRIORITIES} value={x.priority || p} onChange={(v) => !off && run('priority', { priority: v })} label="Priority" block />
      </Sec>
      {!dn && (
        <Sec title="Move to stage">
          <div className="ac-stages" role="radiogroup" aria-label="Stage">
            {OPEN_LANES.map((l) => (
              <button
                key={l.key}
                type="button"
                role="radio"
                aria-checked={x.lane === l.key}
                className={cx('m-fchip', x.lane === l.key && 'on')}
                disabled={Boolean(why)}
                onClick={() => x.lane !== l.key && run('move', { lane: l.key })}
              >
                <span className="aa2-dot" style={{ background: l.color }} />
                {l.label}
              </button>
            ))}
          </div>
        </Sec>
      )}
      {!dn && (
        <Sec title="Log an attempt">
          <div className="ac-atts">
            {ATTEMPTS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className="m-fchip"
                disabled={Boolean(why)}
                onClick={() => run('attempt', { what: key }, false, `Logged: ${label}.`)}
              >
                {label}
              </button>
            ))}
          </div>
        </Sec>
      )}
      <Sec title="Notes" right={<span className="m-sub">{(x.notes || []).length}</span>}>
        {canManage && <NoteBox onAdd={(text) => run('notes', { text })} />}
      </Sec>
      {(x.notes || []).length > 0 && (
        <div className="ac-notes">
          {x.notes.map((n, i) => (
            <div key={i} className="ac-note">
              <div className="m-row">
                <Av name={n.by} cls="sm" />
                <span className="sm b">{n.by}</span>
                <div className="grow" />
                <span className="m-sub">{stampOf(n.at, tz, now)}</span>
              </div>
              <div className="sm">{n.text}</div>
            </div>
          ))}
        </div>
      )}
      <Sec title="Activity">
        <Log rows={[[created, stampOf(x.arrivedAt, tz, now)], ...(x.activity || []).map((a) => [a.text, stampOf(a.at, tz, now)])]} />
      </Sec>
    </div>
  );

  const isMaint = ['maintenance', 'emergency'].includes(x.type) || Boolean(ticket);
  const sched = x.type === 'leasing' ? ['Schedule tour', faKey, 'tour'] : null;
  const callB = x.contact?.phone ? (
    <Btn icon={faPhone} onClick={() => (window.location.href = `tel:${x.contact.phone}`)}>
      Call
    </Btn>
  ) : null;
  const textB = x.contact?.phone ? (
    <Btn icon={faComment} onClick={() => go('/inbox', { contactNumber: x.contact.phone })}>
      Text
    </Btn>
  ) : null;
  const schedB = sched ? (
    <Btn
      icon={sched[1]}
      aria-label={sched[0]}
      onClick={() => onSchedule(sched[2], { name, phone: x.contact?.phone || '', notes: property ? `Showing: ${property}` : '' })}
    >
      {phone ? (sched[2] === 'tour' ? 'Tour' : 'Maintenance') : sched[0]}
    </Btn>
  ) : null;
  const startB =
    !dn && !why && x.lane === 'needs' ? (
      <Btn kind="primary" icon={faPlay} onClick={() => run('move', { lane: 'progress' }, false, 'Started working.')}>
        Start working
      </Btn>
    ) : null;
  const primary =
    !dn && (x.lane === 'review' || x.approve?.label) ? (
      why ? (
        <LockBtn label={x.lane === 'review' ? 'Looks right' : x.approve.label} why={why} />
      ) : (
        <Btn kind="primary" icon={x.lane === 'review' ? faThumbsUp : null} onClick={() => onPrimary(x)}>
          {x.lane === 'review' ? 'Looks right' : x.approve.label}
        </Btn>
      )
    ) : null;
  const editB =
    !dn && !why && x.lane !== 'review' && x.approve?.edit?.length && !editing ? (
      <Btn onClick={() => setEditing(true)}>{x.kind === 'showing' ? 'Edit: move' : 'Edit'}</Btn>
    ) : null;
  const notRight =
    !dn && x.lane === 'review' && !why ? (
      <Btn icon={faThumbsDown} onClick={() => onFlag(x)}>
        Not right
      </Btn>
    ) : null;
  const reopen =
    dn && canManage && x.resolvedBy?.role && ['showing', 'callback', 'callonly', 'batch'].includes(x.kind) ? (
      <Btn onClick={() => run('undo', null, false, 'Moved back to New.')}>Reopen</Btn>
    ) : null;
  const doneB = dn ? (
    <Chip fam="ok" icon={faCircleCheck}>
      Completed
    </Chip>
  ) : why ? (
    <LockBtn label="Complete" why={why} />
  ) : (
    <Btn kind={x.lane === 'needs' || primary ? null : 'primary'} icon={faCheck} onClick={() => onFinish(x)}>
      Complete
    </Btn>
  );
  const foot = phone ? (
    <div className="ac-foot ph2">
      <div className="ac-frow1">
        {primary}
        {editB}
        {notRight}
        {startB}
        {doneB}
        {reopen}
      </div>
      {(callB || textB || schedB) && (
        <div className="ac-frow2">
          {callB}
          {textB}
          {schedB}
        </div>
      )}
    </div>
  ) : (
    <div className="ac-foot">
      {callB}
      {textB}
      {schedB}
      <div className="grow" />
      {notRight}
      {editB}
      {primary}
      {startB}
      {reopen}
      {doneB}
    </div>
  );

  return createPortal(
    <div className={cx('aa', phone && 'ph')}>
      <div className="ac-scrim" onClick={onClose} />
      <div
        className={cx('ac-modal', phone && 'ph')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ac-title"
        tabIndex={-1}
        ref={(el) => {
          if (el && !el.dataset.f) {
            el.dataset.f = '1';
            el.focus();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        {head}
        {chips}
        {isMaint && (
          <Alert
            type="info"
            showIcon
            className="ac-maint"
            message="Scheduling maintenance will need to be conducted outside of the platform at this time"
          />
        )}
        <div className="ac-body">
          {left}
          {right}
        </div>
        {foot}
      </div>
    </div>,
    document.body
  );
}
